/**
 * Canonical Organization + LocalBusiness JSON-LD for Texas Made Shade & Covers.
 *
 * One source of truth for NAP (name / address / phone), geo coordinates,
 * hours, service area and sameAs profile links. The root route emits these
 * nodes site-wide; key service pages re-emit them (identical `@id`s, so
 * search engines merge rather than duplicate) via `businessHeadScripts()`.
 */

import { absUrl, SITE_URL, SITE_NAME } from "@/lib/seo";
import ogDefault from "@/assets/original/hero-shade.jpg";

export const ORG_ID = `${SITE_URL}/#organization`;
export const BUSINESS_ID = `${SITE_URL}/#business`;

export const BUSINESS = {
  name: SITE_NAME,
  alternateName: "Texas Made Shade",
  legalName: "Texas Made Shade & Covers",
  url: SITE_URL,
  telephone: "+1-830-390-9588",
  email: "office@texasmadeshade.com",
  street: "959 East Kingsbury Street",
  city: "Seguin",
  region: "TX",
  postalCode: "78155",
  country: "US",
  latitude: 29.5688,
  longitude: -97.9647,
  foundingDate: "1992",
  priceRange: "$$",
  description:
    "Custom-built awnings, retractable awnings, shade sails, curtains, covers, and aluminum welding — designed, sewn and fabricated in Seguin, Texas.",
} as const;

/** Public profiles and authoritative listings for this business. */
export const SAME_AS = [
  "https://www.facebook.com/profile.php?id=61556233022173",
  "https://www.google.com/maps/search/?api=1&query=Texas+Made+Shade+%26+Covers%2C+959+East+Kingsbury+Street%2C+Seguin%2C+TX+78155",
  "https://texasmadeshade.com",
];

export const HAS_MAP =
  "https://www.google.com/maps/search/?api=1&query=Texas+Made+Shade+%26+Covers%2C+959+East+Kingsbury+Street%2C+Seguin%2C+TX+78155";

const IMAGE = absUrl(ogDefault);

export const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: BUSINESS.street,
  addressLocality: BUSINESS.city,
  addressRegion: BUSINESS.region,
  postalCode: BUSINESS.postalCode,
  addressCountry: BUSINESS.country,
} as const;

export const GEO_COORDINATES = {
  "@type": "GeoCoordinates",
  latitude: BUSINESS.latitude,
  longitude: BUSINESS.longitude,
} as const;

export const OPENING_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
    opens: "08:30",
    closes: "17:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Friday"],
    opens: "08:30",
    closes: "16:00",
  },
];

const city = (name: string, wiki?: string) => ({
  "@type": "City",
  name,
  ...(wiki ? { "@id": wiki } : {}),
  containedInPlace: { "@type": "State", name: "Texas" },
});

const AREA_SERVED = [
  city("Seguin", "https://en.wikipedia.org/wiki/Seguin,_Texas"),
  city("San Antonio", "https://en.wikipedia.org/wiki/San_Antonio"),
  city("New Braunfels"),
  city("Austin"),
  city("San Marcos"),
  city("Boerne"),
  city("Kerrville"),
  city("Fredericksburg"),
  city("Corpus Christi"),
  city("Houston"),
  {
    "@type": "GeoCircle",
    geoMidpoint: GEO_COORDINATES,
    geoRadius: "150000",
    description: "Service radius covering Seguin, San Antonio, and surrounding Central/South Texas.",
  },
];

/**
 * Services we provide, listed as Service nodes rather than Offers.
 * Every job is custom-quoted, so we publish no price — and an Offer without a
 * price is what Google flags, so these stay Services.
 */
const SERVICES_KNOWN = [
  { name: "Custom Awnings", url: `${SITE_URL}/awnings` },
  { name: "Retractable Awnings", url: `${SITE_URL}/retractable-awnings` },
  { name: "Exterior Vertical Shades", url: `${SITE_URL}/exterior-vertical-shades` },
  { name: "Sail Shades", url: `${SITE_URL}/sail-shades` },
  { name: "Patio Covers", url: `${SITE_URL}/pergola-covers` },
  { name: "Boat Covers", url: `${SITE_URL}/boat-covers` },
  { name: "Curtains & Drop Panels", url: `${SITE_URL}/curtains` },
  { name: "Welding & Custom Fabrication", url: `${SITE_URL}/custom-fabrication` },
].map((s) => ({ "@type": "Service", name: s.name, url: s.url, provider: { "@id": BUSINESS_ID } }));


/** Organization node — brand-level identity, logo and sameAs profiles. */
export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    alternateName: BUSINESS.alternateName,
    url: BUSINESS.url,
    logo: {
      "@type": "ImageObject",
      url: IMAGE,
      caption: `${BUSINESS.name} — custom awnings and covers, Seguin, Texas`,
    },
    image: IMAGE,
    description: BUSINESS.description,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    foundingDate: BUSINESS.foundingDate,
    address: POSTAL_ADDRESS,
    areaServed: AREA_SERVED,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        telephone: BUSINESS.telephone,
        email: BUSINESS.email,
        areaServed: "US-TX",
        availableLanguage: ["en", "es"],
      },
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: BUSINESS.telephone,
        email: BUSINESS.email,
        areaServed: "US-TX",
        availableLanguage: ["en", "es"],
      },
    ],
    sameAs: SAME_AS,
    subOrganization: { "@id": BUSINESS_ID },
  };
}

/** LocalBusiness node — physical shop, hours, geo and service area. */
export function localBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": BUSINESS_ID,
    name: BUSINESS.name,
    alternateName: BUSINESS.alternateName,
    url: BUSINESS.url,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    image: IMAGE,
    logo: IMAGE,
    description: BUSINESS.description,
    address: POSTAL_ADDRESS,
    geo: GEO_COORDINATES,
    hasMap: HAS_MAP,
    openingHoursSpecification: OPENING_HOURS,
    areaServed: AREA_SERVED,
    serviceArea: [city("Seguin, TX"), city("San Antonio, TX")],
    priceRange: BUSINESS.priceRange,
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Check, Credit Card",
    foundingDate: BUSINESS.foundingDate,
    sameAs: SAME_AS,
    parentOrganization: { "@id": ORG_ID },
    knowsAbout: SERVICES_KNOWN,
  };
}

/**
 * Ready-to-use `head().scripts` entries for the Organization + LocalBusiness
 * pair. Safe to include on any page — identical `@id`s merge.
 */
export function businessHeadScripts() {
  return [
    { type: "application/ld+json", children: JSON.stringify(organizationJsonLd()) },
    { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd()) },
  ];
}
